import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

import styles from './Shop.module.scss';
import playerImage from '../../assets/images/profile-img.png';

import { request } from '../../shared/request';
import {
    ENV, ERROR_MAINTENANCE,
    OFFER_TYPE_TO_CONFIG_NAME,
    OFFER_TYPES,
    RESOURCES_TYPES_TO_NAME,
} from '../../shared/constants';
import { formatEventPassOffers } from '../../shared/helpers';
import { withResponseHandling, performLogout } from '../../shared/responseHandler';
import { 
    updateOfferCategories, 
    getOffersByType, 
    getAllOffersArray,
    getConfigNameByType,
    wouldHaveVisibleCategory,
} from '../../shared/offersHelpers';
import ShopOffers from './ShopOffers';
import ShopReviewOrder from './ShopReviewOrder';
import Loading from '../../components/Loading/Loading';
import PopupInfo from '../../components/PopupInfo/PopupInfo';
import CratePopupInfo from '../../components/CratePopupInfo/CratePopupInfo';
import ButtonNew from "../../components/buttons/ButtonNew/ButtonNew";

/**
 * Shop Component - Main shop page that displays and manages all offer types
 * 
 * This component handles:
 * - Fetching and displaying various offer types (gems, crates, resources, etc.)
 * - Managing offer selection and purchase flow
 * - Filtering and categorizing offers
 * - Auto-refreshing offers based on expiration times
 * - Handling free offer claims
 * - Managing popup states for crate info and errors
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.offers - Initial offers data for all offer types
 * @param {Function} props.setIsLogged - Function to update login state
 * @param {Object} props.config - Configuration object containing text/labels
 * @param {Object} props.windowSize - Object containing viewport size info (isMobileView, etc.)
 * @param {Function} props.setPlayer - Function to update player state
 * @param {Function} props.updateUnclaimedMilestones - Function to update milestone claim status
 * @param {Function} props.setHasUnclaimedMilestones - Function to set unclaimed milestone flag
 * @param {boolean} props._isMarketingEmailGiven - Initial marketing email consent status
 * @returns {JSX.Element} Shop component
 */
const Shop = ({
    offers,
    setIsLogged,
    config,
    windowSize,
    setPlayer,
    updateUnclaimedMilestones,
    setHasUnclaimedMilestones,
    _isMarketingEmailGiven,
}) => {
    // Retrieve player info and filters from localStorage
    let player = localStorage.getItem('playerInfo');
    player = JSON.parse(player);
    let filters = localStorage.getItem('filters');
    filters = JSON.parse(filters);

    const { state } = useLocation();
    
    // Refs for managing auto-refresh functionality
    let debounceTimeout = useRef(0); // Timeout ID for scheduled refresh
    let refreshAfter = useRef(Date.now() + 900000); // Absolute timestamp of next refresh (15 minutes default)
    
    // State for all different offer types
    const [eventPassOffers, setEventPassOffers] = useState(
        offers.eventPassOffers || []
    );
    const [specialOffers, setSpecialOffers] = useState(
        offers.specialOffers || []
    );
    const [everyDayOffers, setEveryDayOffers] = useState(
        offers.everyDayOffers || []
    );
    const [webShopExclusiveOffers, setWebShopExclusiveOffers] = useState(
        offers.webShopExclusiveOffers || []
    );
    const [gems, setGems] = useState(offers.gems || []);
    const [crates, setCrates] = useState(offers.crates || []);
    const [resources, setResources] = useState(offers.resources || {});
    const [freeOffers, setFreeOffers] = useState(offers.freeOffers || []);
    const [commanderOffers, setCommanderOffers] = useState(offers.commanderOffers || {});
    const [isMarketingEmailGiven, setIsMarketingEmailGiven] = useState(_isMarketingEmailGiven);
    
    // UI state management
    const [selectedOffer, setSelectedOffer] = useState(null); // Currently selected offer for purchase
    const [isLoading, setIsLoading] = useState(true); // Loading state for API calls
    const [error, setError] = useState({}); // Error state for displaying error popups
    const [crateInfoPopUp, setCrateInfoPopUp] = useState({}); // State for crate details popup
    const [offersCategories, setOffersCategories] = useState(filters); // Filter/category state
    
    // Additional offer types
    const [fortuneEventTokens, setFortuneEventTokens] = useState(offers.fortuneEventTokens || []);
    const [gambitLeaguePackages, setGambitLeaguePackages] = useState(offers.gambitLeaguePackages || []);
    const [flares, setFlares] = useState(offers.flares || []);
    const [allOffersArray, setAllOffersArray] = useState([]); // Flattened array of all offers
    
    const navigate = useNavigate();

    /**
     * Logs out the user and clears selected offer
     * Calls performLogout to handle cleanup and navigation
     */
    const logOut = () => {
        removeSelectedOffer();
        performLogout({
            navigate,
            setIsLogged,
            setHasUnclaimedMilestones,
        });
    };

    /**
     * Schedules an automatic refresh of offers based on the soonest expiring offer
     * 
     * @param {Array} offers - Array of offers with End timestamps
     */
    const scheduleRefreshOffers = (offers) => {
        if (!offers.length) return;
        
        // Calculate time until each offer expires
        const offersTimes = offers.map(
            (offer) => Date.parse(offer.End) - Date.now()
        );
        
        // Find the shortest time (soonest expiration)
        const nextRefreshInMs = offersTimes.reduce((a, b) => (a < b ? a : b));

        // Store the absolute timestamp of the next refresh for the tab-focus comparison
        refreshAfter.current = Date.now() + nextRefreshInMs;

        // Clear any existing scheduled refresh
        clearTimeout(debounceTimeout.current);

        // Schedule new refresh at the calculated time (clamped to >= 0)
        debounceTimeout.current = setTimeout(() => {
            getOffers();
        }, Math.max(0, nextRefreshInMs));
    };

    /**
     * Fetches all shop offers and player information from the API
     * Updates all offer states and schedules next refresh
     * Handles errors and authentication issues
     * 
     * @async
     */
    /**
     * Fetches all shop offers and player information from the API
     * Updates all offer states and schedules next refresh
     * Handles errors and authentication issues
     * 
     * @async
     */
    const getOffers = async () => {
        try {
            setIsLoading(true);
            
            await withResponseHandling(
                () => request.get(`getWebShopInfo/${player.id}`),
                {
                    navigate,
                    onSuccess: (response) => {
                        // Update player currency in localStorage
                        const updatePlayerInfo = { ...player };
                        updatePlayerInfo.currency = response.Currency;
                        localStorage.setItem(
                            'playerInfo',
                            JSON.stringify(updatePlayerInfo)
                        );

                        // Collect offers with timers for scheduling next refresh
                        let offersWithTimers = [];
                        if (response.ShopOffers.length) {
                            offersWithTimers = [...response.ShopOffers];
                        }
                        if (response.WebShopExclusiveOffers.length) {
                            offersWithTimers = [...offersWithTimers, ...response.WebShopExclusiveOffers];
                        }

                        // Update all offer states
                        setWebShopExclusiveOffers(response.WebShopExclusiveOffers);
                        setEventPassOffers(
                            formatEventPassOffers(response.BattlePassOffers)
                        );
                        setSpecialOffers(response.ShopOffers || []);
                        setEveryDayOffers(response.EveryDayOffers || []);
                        setGems(response.GemsPackages || []);
                        setCrates(response.Crates || []);
                        setResources(response.ExpeditionResources || {});
                        setFreeOffers(response.FreeOffers || []);
                        setCommanderOffers(response.CommanderOffers || {});
                        setFortuneEventTokens(response.FortuneEventTokens || [])
                        setGambitLeaguePackages(response.GambitLeaguePackages || [])
                        setFlares(response.MascotDropsTokens || [])

                        // Set the marketing email given status
                        setIsMarketingEmailGiven(response.IsMarketingEmailGiven);
                        
                        // Update the player info in parent component
                        setPlayer(updatePlayerInfo);

                        // Generate flattened array of all offers using helper
                        const offersData = {
                            specialOffers: response.ShopOffers || [],
                            everyDayOffers: response.EveryDayOffers || [],
                            gems: response.GemsPackages || [],
                            crates: response.Crates || [],
                            resources: response.ExpeditionResources || {},
                            freeOffers: response.FreeOffers || [],
                            commanderOffers: response.CommanderOffers || {},
                            fortuneEventTokens: response.FortuneEventTokens || [],
                            gambitLeaguePackages: response.GambitLeaguePackages || [],
                            flares: response.MascotDropsTokens || [],
                            eventPassOffers: formatEventPassOffers(response.BattlePassOffers),
                            webShopExclusiveOffers: response.WebShopExclusiveOffers || [],
                        };
                        setAllOffersArray(getAllOffersArray(offersData));

                        // Check for unclaimed milestones
                        if (response.Milestones && response.Milestones.length) {
                            const unclaimedMilestone = response.Milestones.find(milestone => !milestone.Claimed && (milestone.CollectedWarTokens >= milestone.RequiredTokens))
                            if (unclaimedMilestone) {
                                updateUnclaimedMilestones(true);
                            }
                        }

                        // Schedule next refresh based on offer expiration times
                        if (offersWithTimers.length) {
                            scheduleRefreshOffers(offersWithTimers);
                        }
                    },
                    onAccessDenied: () => {
                        logOut();
                    },
                    onEmpty: () => {
                        logOut();
                    },
                    onError: (err) => {
                        setError({
                            hasError: true,
                            message: ENV === 'test' ? err.message : null,
                        });
                    },
                }
            );
        } catch (err) {
            setError({
                hasError: true,
                message: ENV === 'test' ? err.message : null,
            });
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Finds an offer by its ID from the flattened offers array
     * 
     * @param {string|number} id - The offer ID to search for
     * @returns {Object|null} The found offer object or null if not found
     */
    /**
     * Finds an offer by its ID from the flattened offers array
     * 
     * @param {string|number} id - The offer ID to search for
     * @returns {Object|null} The found offer object or null if not found
     */
    const getOfferById = (id) => {
        const offer = allOffersArray.find((offer) => offer.OfferId === id);
        if (offer) {
            return offer;
        }

        return null;
    };

    /**
     * Removes the selected offer from the state and localStorage.
     */
    const removeSelectedOffer = () => {
        setSelectedOffer(null);
        localStorage.removeItem('selectedOffer');
    }

    /**
     * Selects an offer by ID and type, preparing it for purchase
     * Handles special cases for resources, commanders, and crates
     * Adds additional mapped properties for display
     * 
     * @param {string|number} id - The offer ID
     * @param {string} type - The offer type (from OFFER_TYPES constants)
     * @param {string} image - Optional image URL to map
     * @param {string} name - Optional name to map
     * @param {number} quantity - Optional quantity to map
     * @param {string} resourceType - Optional resource type for resource offers
     * @param {string} eventPassImgUrl - Optional event pass image URL
     * @param {string} imgBackground - Optional background image
     */
    const selectOfferById = (
        id,
        type,
        image,
        name,
        quantity,
        resourceType,
        eventPassImgUrl, 
        imgBackground
    ) => {
        // Prepare offers state object for helper function
        const offersState = {
            fortuneEventTokens,
            gambitLeaguePackages,
            flares,
            webShopExclusiveOffers,
            eventPassOffers,
            specialOffers,
            everyDayOffers,
            gems,
            crates,
            resources,
            commanderOffers,
            freeOffers,
        };

        let offers = getOffersByType(type, offersState);

        // Special handling for resources - need to access nested object by resource type
        if (type === OFFER_TYPES.resources && resourceType) {
            const resourcesType = RESOURCES_TYPES_TO_NAME[resourceType];
            offers = resources[resourcesType] || [];
        }

        // Special handling for commanders - they use the Experience property
        if (type === OFFER_TYPES.commanders) {
            offers = commanderOffers.Experience || [];
        }

        // Special handling for crates - combine regular crates and commander crates
        if (type === OFFER_TYPES.crates) {
            const allCrates = [...crates];
            if (commanderOffers && commanderOffers.Crates && Array.isArray(commanderOffers.Crates)) {
                allCrates.push(...commanderOffers.Crates);
            }
            offers = allCrates;
        }

        // Convert id to string for comparison since API returns OfferId as strings
        const idString = String(id);
        let offerToSelect = offers.find((offer) => String(offer.OfferId) === idString);

        if (!offerToSelect) {
            console.error(`Offer with id ${id} and type ${type} not found`);
            console.error('Searched in offers array:', offers);
            return;
        }

        // Map additional properties for display purposes
        if (image) offerToSelect['mappedImg'] = image;
        if (name) offerToSelect['mappedName'] = name;
        if (quantity) offerToSelect['mappedQuantity'] = quantity;
        if (eventPassImgUrl) offerToSelect['ImageURL'] = eventPassImgUrl;
        if (imgBackground) offerToSelect['imgBackground'] = imgBackground;
        
        // Store selected offer info in localStorage for persistence
        const offerInfo = {
            offerId: offerToSelect.OfferId,
            offerType: offerToSelect.Type,
        };
        localStorage.setItem('selectedOffer', JSON.stringify(offerInfo));
        setSelectedOffer(offerToSelect);
    };

    /**
     * Event handler for when the browser tab gains focus
     * Refreshes offers if they have expired while tab was inactive
     * This fixes issues where browser marks inactive tabs and timers don't fire
     * 
     * @param {Event} event - The focus event
     */
    /**
     * Event handler for when the browser tab gains focus
     * Refreshes offers if they have expired while tab was inactive
     * This fixes issues where browser marks inactive tabs and timers don't fire
     * 
     * @param {Event} event - The focus event
     */
    const getIsTabFocused = (event) => {
        // If the refreshAfter timestamp is somewhere in the future, the offer is not expired - don't refresh
        if (event.type === 'focus' && refreshAfter.current < Date.now()) {
            getOffers();
        }
    };

    /**
     * Updates the offer categories visibility based on available offers
     * Uses helper function to determine which categories should be shown
     */
    const updateOffersCategories = () => {
        // Special Events (flares, fortuneEventTokens, gambitLeaguePackages) is a
        // grouped category; updateOfferCategories collapses those sub-offers into
        // the single specialEvents chip based on the config's groupKey/subKeys.
        const offersData = {
            fortuneEventTokens,
            gambitLeaguePackages,
            flares,
            webShopExclusiveOffers,
            eventPassOffers,
            specialOffers,
            everyDayOffers,
            gems,
            crates,
            resources,
            commanderOffers,
            freeOffers,
        };

        const updatedCategories = updateOfferCategories(offersCategories, offersData);
        setOffersCategories(updatedCategories);
    };
    

    /**
     * Toggles the visibility of a specific offer category in the filter
     * Ensures at least one category remains visible
     * 
     * @param {string} type - The offer type to toggle (from OFFER_TYPES constants)
     */
    /**
     * Toggles the visibility of a specific offer category in the filter
     * Ensures at least one category remains visible
     * 
     * @param {string} type - The offer type to toggle (from OFFER_TYPES constants)
     */
    const filterCategory = (type) => {
        const typeName = getConfigNameByType(type);
        if (!typeName) return;
        
        const filteredOffers = { ...offersCategories };
        
        // Check if we would have at least one visible category after toggle
        if (!wouldHaveVisibleCategory(filteredOffers, type)) {
            setError({ hasError: true, message: config.filterErr });
            return;
        }
        
        // Toggle visibility and persist to localStorage
        filteredOffers[typeName].isVisible = !filteredOffers[typeName].isVisible;
        localStorage.setItem('filters', JSON.stringify(filteredOffers));
        setOffersCategories(filteredOffers);
    };

    /**
     * Opens the crate info popup with the provided crate details
     * 
     * @param {Object} info - Crate information to display in popup
     */
    const openCrateInfoPopUp = (info) => {
        // Set the info to display in popup
        setCrateInfoPopUp(info);
    };

    /**
     * Claims a free offer for the player
     * Navigates to checkout result page with success/failure status
     * 
     * @async
     * @param {string|number} offerId - The ID of the free offer to claim
     * @param {string} offerType - The type of the free offer
     */
    /**
     * Claims a free offer for the player
     * Navigates to checkout result page with success/failure status
     * 
     * @async
     * @param {string|number} offerId - The ID of the free offer to claim
     * @param {string} offerType - The type of the free offer
     */
    const claimFreeOffer = async (offerId, offerType) => {
        try {
            setIsLoading(true);
            
            await withResponseHandling(
                () => request.post(`claimFreeOffer/${player.id}`, {
                    offerId: offerId,
                    offerType: offerType,
                }),
                {
                    navigate,
                    onSuccess: (response) => {
                        if (response.Success) {
                            navigate('/checkout-result', {
                                state: { success: true, isFreeOffer: true },
                            });
                        } else {
                            navigate('/checkout-result', {
                                state: { success: false, isFreeOffer: true },
                            });
                        }
                    },
                    onError: (err) => {
                        setError({
                            hasError: true,
                            message: ENV === 'test' ? err.message : null,
                        });
                    },
                }
            );
        } catch (err) {
            setError({
                hasError: true,
                message: ENV === 'test' ? err.message : null,
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Effect: Initial setup and offer fetching
    // Logs out if no player info exists, otherwise fetches offers if not already loaded
    // Effect: Initial setup and offer fetching
    // Logs out if no player info exists, otherwise fetches offers if not already loaded
    useEffect(() => {
        if (!player) {
            logOut();
        }
        if (!specialOffers.length || !gems.length) {
            getOffers();
        } else {
            setIsLoading(false);
            scheduleRefreshOffers(specialOffers);
        }
    }, []);

    // Effect: Set up tab focus listener for refreshing offers
    // Cleans up listener on unmount
    useEffect(() => {
        window.addEventListener('focus', getIsTabFocused);

        return () => {
            window.removeEventListener('focus', getIsTabFocused);
        };
    }, [getIsTabFocused]);


    // Effect: Update offer categories when any offer type changes
    // This ensures the filter categories reflect current offer availability
    useEffect(() => {
        if (
            !fortuneEventTokens.length ||
            !gambitLeaguePackages.length ||
            !flares.length ||
            !webShopExclusiveOffers.length ||
            !eventPassOffers.length ||
            !specialOffers.length ||
            !everyDayOffers.length ||
            !gems.length ||
            !crates.length ||
            !resources.length ||
            !Object.keys(commanderOffers).length
        ) {
            updateOffersCategories();
        }
    }, [
        gambitLeaguePackages,
        fortuneEventTokens,
        flares,
        webShopExclusiveOffers,
        specialOffers,
        everyDayOffers,
        gems,
        crates,
        resources,
        eventPassOffers,
        commanderOffers,
    ]);

    // Effect: Handle navigation state changes
    // Refreshes offers if refreshShop flag is set in navigation state
    useEffect(() => {
        if (state && state.refreshShop) {
            getOffers();
        }
    }, [state]);

    // Render loading screen while fetching data
    return isLoading ? (
        <Loading />
    ) : (
        <div className={styles.wrapper}>
            {/* Mobile view: Display player info bar with logout button */}
            {windowSize.isMobileView ? (
                <div className={styles.infoBar}>
                    <div className={styles.player}>
                        <img
                            src={playerImage}
                            alt={''}
                            className={styles.playerImg}
                        />
                        <div className={styles.playerInfo}>
                            <div className={styles.playerName}>
                                {player.name}
                            </div>
                            <div
                                className={styles.playerId}
                            >{`#${player.id}`}</div>
                        </div>
                    </div>
                    <ButtonNew
                        text={config.logout}
                        isSmall={true}
                        onClick={logOut}
                    />
                </div>
            ) : null}
            
            {/* Conditional rendering: Show either review order or shop offers.
                AnimatePresence (wait) fades the offers out before the review
                order slides in, and vice-versa. */}
            <AnimatePresence exitBeforeEnter>
            {selectedOffer ? (
                <motion.div
                    key="review"
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 24 }}
                    transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                >
                <ShopReviewOrder
                    {...selectedOffer}
                    setSelectedOffer={setSelectedOffer}
                    config={config}
                    windowSize={windowSize}
                    currency={player.currency}
                    selectedOffer={selectedOffer}
                    IsMarketingEmailGiven={isMarketingEmailGiven}
                    removeSelectedOffer={removeSelectedOffer}
                />
                </motion.div>
            ) : (
                <motion.div
                    key="offers"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                >
                <ShopOffers
                    eventPassOffers={eventPassOffers}
                    freeOffers={freeOffers}
                    specialOffers={specialOffers}
                    everyDayOffers={everyDayOffers}
                    gems={gems}
                    crates={crates}
                    resources={resources}
                    commanderOffers={commanderOffers}
                    webShopExclusiveOffers={webShopExclusiveOffers}
                    fortuneEventTokens={fortuneEventTokens}
                    gambitLeaguePackages={gambitLeaguePackages}
                    flares={flares}
                    selectOfferById={selectOfferById}
                    getOffers={getOffers}
                    config={config}
                    windowSize={windowSize}
                    currency={player.currency}
                    offersCategories={offersCategories}
                    filterCategory={filterCategory}
                    openCrateInfoPopUp={openCrateInfoPopUp}
                    claimFreeOffer={claimFreeOffer}
                />
                </motion.div>
            )}
            </AnimatePresence>

            {/* Error popup - shown when error.hasError is true */}
            <AnimatePresence>
                {error.hasError ? (
                    <PopupInfo text={error.message} setError={setError} />
                ) : null}
            </AnimatePresence>

            {/* Crate info popup - shown when crateInfoPopUp has data */}
            <AnimatePresence>
                {Object.keys(crateInfoPopUp).length ? (
                    <CratePopupInfo
                        info={crateInfoPopUp}
                        setCrateInfoPopUp={setCrateInfoPopUp}
                        selectOfferById={selectOfferById}
                    />
                ) : null}
            </AnimatePresence>
        </div>
    );
};

export default Shop;
